/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { PrintOptionFilterOptionDto } from '@/api-sdk';
import { iconStopwatch } from '@/components/icons';
import { baseStyles } from '@/styles';

import '@/components/image/fg-image';

@customElement('fg-print-option-filter-color-option')
export class FgPrintOptionFilterColorOption extends LitElement {
  @property({ type: Number })
  subStepIndex: number = 0;

  @property({ type: Object })
  option?: PrintOptionFilterOptionDto;

  render() {
    if (!this.option) {
      return null;
    }

    const { name, image, queryString, hspAvailable } = this.option;

    return html`
      <button class="option" @click=${() => this.selectOption(queryString)}>
        ${image &&
        html`
          <div class="option-image">
            <fg-image
              alt=${name}
              height="20"
              src=${image}
              width="100"
            ></fg-image>
          </div>
        `}

        <strong class="option-label">${name}</strong>

        ${hspAvailable
          ? html`
              <span class="option-hsp">
                ${iconStopwatch} ${t('labels.deliveredQuickly')}
              </span>
            `
          : nothing}
      </button>
    `;
  }

  selectOption(queryString: PrintOptionFilterOptionDto['queryString']) {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString,
            code: this.option?.code,
            subStepIndex: this.subStepIndex,
          },
        },
      }),
    );
  }

  static styles = [
    baseStyles,
    css`
      .option {
        height: 100%;
        min-height: 80px;
        background: #fff;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
      }

      .option:hover {
        color: var(--fg-primary, var(--fg-fallback-primary));
        border-color: var(--fg-primary, var(--fg-fallback-primary));
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
      }

      .option-label {
        font-size: 1rem;
        line-height: 1.6;
      }

      .option-hsp {
        display: flex;
        align-items: center;
        margin-top: 0.25rem;
        gap: 3px;
      }
    `,
  ];
}
